import { useEffect, useMemo } from "react";

import analytics from "@analytics/AppInsightsService";

import { sessionConstants } from "@constants/appConstants";

import useNumberFormat from "@hooks/useNumberFormat";
import { useWebPubSubListener } from "@hooks/useWebPubSub";

import { loadExternalScript } from "@utils/appUtils";
import DupontLogger from "@utils/DupontLogger";

import { WPFullScreenLoader } from "@components/WPFullScreenLoader";
import { WPFullScreenPopup } from "@components/WPFullScreenPopup";

import MinervaIntegration from "@features/minervaIntegration/minerva";
import { getMldJobMap } from "@features/report-new/common/hooks/mldJobMap.utils";
import { clearAllLoginSessions } from "@features/sso/loginEngagementSession.utils";

import { cancelMLDReport } from "./api";
import { initializeLocal } from "./i18n";
import Routing from "./Routing";

import "./i18n";

const App = () => {
  initializeLocal();
  useWebPubSubListener();
  useNumberFormat();
  const trustArcCookieURL = import.meta.env.VITE_TRUSTARC_CONSENT_URL;
  const Logger = DupontLogger("App");

  if (trustArcCookieURL) {
    loadExternalScript(trustArcCookieURL);
  }

  const isMinervaEnabled = useMemo(() => import.meta.env.VITE_MINERVA_ENABLED === "true", []);

  useEffect(() => {
    analytics.initAzureAnalytics(import.meta.env.VITE_INSTRUMENTATION_KEY, null);
  }, []);

  useEffect(() => {
    /**
     * Handles page/tab/browser termination events for login engagement session cleanup.
     *
     * We set a per-tab marker in sessionStorage to avoid duplicate cleanup calls
     * when both `beforeunload` and `pagehide` fire during the same transition.
     * If the marker already exists, cleanup is skipped for that transition.
     * Otherwise, we mark it and clear login engagement entries from localStorage.
     *
     * @returns {void}
     */
    const handleWindowClosing = () => {
      const pendingJobIds = Object.values(getMldJobMap());
      sessionStorage.removeItem(sessionConstants.mldReportJobMap);
      pendingJobIds.forEach(jobId => {
        cancelMLDReport(jobId).catch(() => {
          Logger.info("Not capturing Engagement payload on DEV/SIT environment:");
        });
      });
      const isRefreshed = sessionStorage.getItem("wavepro_login_session_marker") === "refreshed";
      if (isRefreshed) return;

      sessionStorage.setItem("wavepro_login_session_marker", "refreshed");
      clearAllLoginSessions();
    };

    window.addEventListener("beforeunload", handleWindowClosing);
    window.addEventListener("pagehide", handleWindowClosing);

    return () => {
      window.removeEventListener("beforeunload", handleWindowClosing);
      window.removeEventListener("pagehide", handleWindowClosing);
    };
  }, []);

  return (
    <>
      <Routing />
      <WPFullScreenLoader />
      <WPFullScreenPopup />
      {isMinervaEnabled && <MinervaIntegration />}
    </>
  );
};

export default App;
